import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { GoogleClassroomService } from '../../services/GoogleClassroom.service';
import { Toast } from '../../components/Common/Toast';

interface GoogleClassroomCallbackProps {}

const GoogleClassroomCallback: React.FC<GoogleClassroomCallbackProps> = ({}: GoogleClassroomCallbackProps) => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setIsLoading(true);

        const searchParams = new URLSearchParams(window.location.search);

        // Extract OAuth parameters from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for OAuth errors
        if (error) {
          console.error('Google Classroom OAuth error:', error, errorDescription);
          setError(errorDescription || 'OAuth authorization failed');
          return;
        }

        // Validate required parameters
        if (!code) {
          setError('Authorization code is missing');
          return;
        }

        console.log('Processing Google Classroom callback with code:', code);

        // Handle the OAuth callback
        const authResponse = await GoogleClassroomService.handleCallback(code, state || undefined);

        console.log('Auth response received:', authResponse);

        // Complete the authentication flow (signs in to Firebase and stores token)
        await GoogleClassroomService.completeAuth(authResponse, dispatch);

        // Show success message
        Toast(authResponse.isNewUser ? 'Account created successfully!' : 'Login successful!');

        // Close popup and send message to parent window if this is a popup
        if (window.opener) {
          window.opener.postMessage(
            { type: 'GOOGLE_CLASSROOM_AUTH_SUCCESS' },
            window.location.origin
          );
          window.close();
        } else {
          // Navigate to home page if not a popup
          navigate('/');
        }
      } catch (error: any) {
        console.error('Error handling Google Classroom callback:', error);
        setError(error.message || 'Authentication failed');

        // Send error message to parent window if this is a popup
        if (window.opener) {
          window.opener.postMessage(
            { type: 'GOOGLE_CLASSROOM_AUTH_ERROR', error: error.message || 'Authentication failed' },
            window.location.origin
          );
          window.close();
        }
      } finally {
        setIsLoading(false);
      }
    };

    handleCallback();
  }, [navigate, dispatch]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col font-sofiaSans justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Connecting to Google Classroom
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Please wait while we complete your authentication...
              </p>
              <div className="mt-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col font-sofiaSans justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Authentication Failed
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {error}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleClassroomCallback;
